// Components==============
import indexActivity from 'actions/activity/index';
import indexFeedback from 'actions/feedback/index';
import indexPortfolio from 'actions/portfolio/index';
import indexProject from 'actions/project/index';
import indexTheme from 'actions/theme/index';
import indexTrainingSubscription from 'actions/training/subscription/index';
import indexTraining from 'actions/training/training/index';
import getAssessedEvidence from 'actions/evidence/getAssessedEvidence';
import FilterMenu from 'components/Layout/FilterMenu/FilterMenu';
import Layout from 'components/Layout/Layout';
import Container from 'components/UI/Container/Container';
import NoContent from 'components/UI/NoContent/NoContent';
import Title from 'components/UI/Title/Title';
import { AnimatePresence } from 'framer-motion';
import { useFilter } from 'hooks/useFilter';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import React, { createContext, useEffect, useState } from 'react';
import { reduceFilter, sortFilter } from 'utilities/filters';
import { getCorrectPhase } from 'utilities/getCorrectPhase';
import { getDate } from 'utilities/getDate';
import { EvidenceEvidenceGroupType, EvidenceGroupType } from '../../../typings';
import AddModal from './components/AddModal/AddModal';
import EvidenceGroup from './components/EvidenceGroup/EvidenceGroup';
import './MyWork.module.scss';
import { MyWorkFunctions, useMyWorksFunctions } from './useMyWorkFunctions';

export type MyWorkContextType = {
  evidence: EvidenceGroupType[];
  portfolios?: (PortfolioEntity | undefined)[];
  selectedPortfolios: number[];
  setSelectedPortfolios: React.Dispatch<React.SetStateAction<number[]>>;
  selectedEvidenceIds: number[];
  setSelectedEvidenceIds: React.Dispatch<React.SetStateAction<number[]>>;
  addModalIsOpen: boolean;
  setAddModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const MyWorkContext = createContext(
  {} as MyWorkContextType & MyWorkFunctions
);

export default function MyWork() {
  const dispatch = useReduxAction();

  const [assessedEvidenceIds, setAssessedEvidenceIds] = useState([]);

  useEffect(() => {
    dispatch(indexTrainingSubscription({}));
    dispatch(indexTraining());
    dispatch(indexPortfolio());
    dispatch(indexProject());
    dispatch(indexActivity());
    dispatch(indexFeedback());
    dispatch(indexTheme());
    dispatch(getAssessedEvidence()).then((d) => setAssessedEvidenceIds(d));
  }, []);

  // Filter logic
  const { filterValues, setFilterValues, initial } = useFilter('my-work');

  const f1 = sortFilter(filterValues[0].value as string);

  const activityFilter = filterValues[0].value === 'project.terms.activities';
  const processFilter = filterValues[0].value === 'project.terms.process-steps';

  const phaseFilterValue = filterValues[1].value as number[];
  const activityFilterValue = filterValues[2].value as number[];

  // add review filter
  // TODO: implement review filter
  const assessedFilterValue = filterValues[3].value as boolean;

  const userId = useRedux((state) => state.authentication?.user?.id);

  const evidence = useRedux((state) => {
    let evidenceList: EvidenceGroupType[] = [];

    // training evidence
    const trainingSubscriptions = state.trainingSubscription?.entities.allIds.map(
      (id) => state.trainingSubscription?.entities.byId[id]
    );

    trainingSubscriptions
      ?.filter((t) => t?.attendances?.length)
      .forEach((t) => {
        if (!t) return;
        const trainingSubscription = t;

        const trainingEvidenceList: EvidenceEvidenceGroupType[] = [];

        trainingSubscription.attendances!.forEach((a) => {
          a.evidence.forEach((e) => {
            if (e.created_by !== userId) return;

            trainingEvidenceList.push({ evidence: e, lesson: a.lesson_id });
          });
        });

        const evidenceEntity: EvidenceGroupType = {
          title:
            state.training?.entities.byId[trainingSubscription.training_id]
              ?.title,
          id: trainingSubscription.training_id,
          evidence: trainingEvidenceList,
          type: 'training',
          updated_at:
            state.training?.entities.byId[trainingSubscription.training_id]
              ?.updated_at,
        };

        if (trainingEvidenceList.length) evidenceList.push(evidenceEntity);
      });

    // Project evidence
    const allProjects = state.project?.entities.allIds.map(
      (projectId) => state.project?.entities.byId[projectId]
    );

    const myProjects = allProjects
      ?.filter((project) => !!project?.in_group)
      .filter(
        (p) =>
          p?.members.find((m) => m.user_id === userId)?.membership !== 'teacher'
      );

    myProjects?.forEach((project) => {
      let currentPhaseId = 0;

      const projectEvidenceList: EvidenceEvidenceGroupType[] = [];

      project?.process?.forEach((p) => {
        const process = state.process?.entities.byId[p];

        if (process?.process_type === 'App\\Phase')
          currentPhaseId = process.process_id;

        // handle phase filter
        if (
          phaseFilterValue.length &&
          !phaseFilterValue.includes(currentPhaseId)
        )
          return;

        // handle activity filter
        if (
          activityFilterValue.length &&
          !activityFilterValue.includes(process?.process_id || 0)
        )
          return;

        // handleIsAssessedFilter
        if (assessedFilterValue) {
          if (!assessedEvidenceIds.includes(process?.id)) {
            return;
          }
        }

        if (!process?.evidence?.length) return;

        // handle activity-sort filter
        if (activityFilter) {
          return process.evidence?.forEach((e) => {
            const title = e.process_id
              ? state.activity?.entities.byId[process.process_id]?.name
              : undefined;

            // if group doesn't exists make new group
            if (
              evidenceList.find((list) => list.title === title) === undefined
            ) {
              const activityGroupEntity: EvidenceGroupType = {
                title,
                id: project?.id,
                evidence: [{ evidence: e }],
                type: 'project',
                updated_at: project.updated_at,
              };
              return evidenceList.push(activityGroupEntity);
            }

            // if group does exists add to group
            return (evidenceList = evidenceList.map((list) =>
              list.title === title
                ? { ...list, evidence: [...list.evidence, { evidence: e }] }
                : list
            ));
          });
        }

        // handle phase-sort filter
        if (processFilter) {
          const phase = getCorrectPhase(currentPhaseId - 1);

          return process.evidence?.forEach((e) => {
            // if group doesn't exists make new group
            if (
              evidenceList.find((list) => list.title === phase) === undefined
            ) {
              const phaseGroupEntity: EvidenceGroupType = {
                title: phase,
                id: project?.id!,
                evidence: [{ evidence: e }],
                type: 'project',
                updated_at: project.updated_at,
              };

              return evidenceList.push(phaseGroupEntity);
            }

            // if group does exists add to group
            evidenceList = evidenceList.map((list) =>
              list.title === phase
                ? { ...list, evidence: [...list.evidence, { evidence: e }] }
                : list
            );
          });
        }
        // handle default-sort filter 1
        process.evidence?.forEach((e) =>
          projectEvidenceList.push({ evidence: e })
        );
      });

      // handle default-sort filter 2
      if (!project?.id) return;
      const projectGroupEntity: EvidenceGroupType = {
        title: project?.theme_id
          ? state.theme?.entities.byId[project?.theme_id]?.name
          : undefined,
        id: project?.id,
        evidence: projectEvidenceList,
        type: 'project',
        updated_at: project?.updated_at,
      };

      // handle default-sort filter 3
      if (projectEvidenceList.length) evidenceList.push(projectGroupEntity);
    });

    return reduceFilter(
      [f1],
      // only show evidence with training or project
      evidenceList
        .filter((e) => e.title)
        .sort((a, b) => getDate(b.updated_at) - getDate(a.updated_at))
    );
  });

  const loading = useRedux(
    (state) =>
      state.activity?.loading ||
      state.training?.loading ||
      state.project?.loading ||
      state.trainingSubscription?.loading ||
      state.portfolio?.loading ||
      state.feedback?.loading ||
      state.theme?.loading
  );

  const portfolios = useRedux((state) =>
    state.portfolio?.entities.allIds
      .map((id) => state.portfolio?.entities.byId[id])
      .filter((portfolio) => portfolio?.created_by === userId)
  );

  const [selectedPortfolios, setSelectedPortfolios] = useState<number[]>([]);

  const [selectedEvidenceIds, setSelectedEvidenceIds] = useState<number[]>([]);

  const [addModalIsOpen, setAddModalIsOpen] = useState(false);

  const contextValues = {
    evidence,
    portfolios,
    selectedPortfolios,
    setSelectedPortfolios,
    selectedEvidenceIds,
    setSelectedEvidenceIds,
    addModalIsOpen,
    setAddModalIsOpen,
    assessedEvidenceIds,
  };

  const functions = useMyWorksFunctions(contextValues);

  return (
    <Layout
      filter={
        <FilterMenu
          filterValues={filterValues}
          setFilterValues={setFilterValues}
          initial={initial}
        />
      }
    >
      <NoContent
        page="my-work"
        hasContent={evidence ? evidence?.length > 0 : false}
        loading={!!loading}
      >
        <MyWorkContext.Provider value={{ ...functions, ...contextValues }}>
          <Container>
            <Title text="general.terms.my-work" loading={loading} />
            <div styleName="wrapper">
              {evidence?.map((e, i) => (
                <EvidenceGroup evidenceGroup={e} key={i} />
              ))}
            </div>

            <AnimatePresence>{addModalIsOpen && <AddModal />}</AnimatePresence>
          </Container>
        </MyWorkContext.Provider>
      </NoContent>
    </Layout>
  );
}
